import lang from 'lang'
import settingsConfig from 'settings/config'
import request from '@/modules/base/util/request'

import { toast } from '@/modules/base/components/toast/toastConfig'
import { errorCodes } from '@/modules/base/util/constants'

// actions
const SHOW_LOADER = 'settings/payConditions/SHOW_LOADER'
const HIDE_LOADER = 'settings/payConditions/HIDE_LOADER'
const SHOW_MODAL_LOADER = 'settings/payConditions/SHOW_MODAL_LOADER'
const HIDE_MODAL_LOADER = 'settings/payConditions/HIDE_MODAL_LOADER'
const OPEN_FORM_MODAL = 'settings/payConditions/OPEN_FORM_MODAL'
const CLOSE_FORM_MODAL = 'settings/payConditions/CLOSE_FORM_MODAL'
const SET_PAY_CONDITIONS = 'settings/payConditions/SET_PAY_CONDITIONS'
const SET_SELECTED_PAY_CONDITIONS = 'settings/payConditions/SET_SELECTED_PAY_CONDITIONS'
const RESET_PAY_CONDITIONS = 'settings/payConditions/RESET_PAY_CONDITIONS'
// reducer
const initialState = {
  isLoading: false,
  formModalOpen: false,
  isModalLoading: false,
  conditions: [],
  selectedCondition: undefined,
}

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case SHOW_LOADER:
      return { ...state, isLoading: true }

    case HIDE_LOADER:
      return { ...state, isLoading: false }

    case SHOW_MODAL_LOADER:
      return { ...state, isModalLoading: true }

    case HIDE_MODAL_LOADER:
      return { ...state, isModalLoading: false }

    case OPEN_FORM_MODAL:
      const { selectedCondition } = action
      return { ...state, formModalOpen: true, selectedCondition }

    case CLOSE_FORM_MODAL:
      return { ...state, formModalOpen: false, selectedCondition: undefined }

    case SET_SELECTED_PAY_CONDITIONS: {
      const { selectedCondition } = action
      return { ...state, selectedCondition }
    }

    case RESET_PAY_CONDITIONS: {
      return { ...state, selectedCondition: undefined }
    }

    case SET_PAY_CONDITIONS:
      const { data } = action
      return { ...state, ...data }

    default:
      return state
  }
};

// action creators
const showLoader = () => {
  return {
    type: SHOW_LOADER,
  }
}

const hideLoader = () => {
  return {
    type: HIDE_LOADER,
  }
}

const showModalLoader = () => {
  return {
    type: SHOW_MODAL_LOADER,
  }
}

const hideModalLoader = () => {
  return {
    type: HIDE_MODAL_LOADER,
  }
}

export const openFormModal = (condition) => ({
  type: OPEN_FORM_MODAL,
  selectedCondition: condition,
})

export const closeFormModal = () => ({
  type: CLOSE_FORM_MODAL,
})

export const resetPayConditions = () => ({
  type: RESET_PAY_CONDITIONS,
})

const setPayConditions = (data) => {
  return {
    type: SET_PAY_CONDITIONS,
    data,
  }
}

export const setSelectedPayConditions = (condition) => ({
  type: SET_SELECTED_PAY_CONDITIONS,
  selectedCondition: condition,
})

export const fetchPayConditions = () => {
  return (dispatch) => {
    dispatch(showLoader())
    return request(settingsConfig.payConditions, { auth: true, method: 'GET', isConcurrent: true })
      .then((response) => {
        const { body: { data: conditions } } = response
        dispatch(setPayConditions({ conditions }))
        dispatch(hideLoader())
        return response
      })
      .catch(() => {
        dispatch(hideLoader())
      })
  }
}

export const createCondition = (data) => {
  return (dispatch, getState) => {
    const { settings: { payConditions: { conditions } } } = getState()
    dispatch(showModalLoader())

    const requests = [
      request(`${settingsConfig.payConditions}`, { auth: true, method: 'POST', data }),
    ]

    // set isDefault false if new condition is default
    const currentDefaultCondition = conditions.find(condition => condition.isDefault)
    if (data.isDefault) {
      currentDefaultCondition.isDefault = false
      requests.push(request(`${settingsConfig.payConditions}/${currentDefaultCondition.id}`, { auth: true, method: 'PUT', data: { isDefault: false } }))
    }

    return Promise.all(requests)
      .then(([createConditionResponse]) => {
        const { id } = createConditionResponse.body.data
        const createdCondition = { ...data, id }

        dispatch(setPayConditions({ conditions: [...conditions, createdCondition] }))
        dispatch(closeFormModal())
        dispatch(setSelectedPayConditions(createdCondition))
        toast.showToast({ message: lang.payConditionCreateSuccessMessage })
        return createdCondition
      })
      .catch(() => {
        toast.showToast({ type: 'error', message: lang.defaultErrorMessage })
        return Promise.reject()
      })
      .finally(() => dispatch(hideModalLoader()))
  }
}

export const updateCondition = (id, data) => {
  return (dispatch, getState) => {
    dispatch(showModalLoader())
    const { settings: { payConditions: { conditions } } } = getState()

    const requests = [
      request(`${settingsConfig.payConditions}/${id}`, { auth: true, method: 'PUT', data }),
    ]

    // set isDefault false if other condition is new default
    const currentDefaultCondition = conditions.find(condition => condition.isDefault)
    if (data.isDefault && currentDefaultCondition.id !== id) {
      currentDefaultCondition.isDefault = false
      requests.push(request(`${settingsConfig.payConditions}/${currentDefaultCondition.id}`, { auth: true, method: 'PUT', data: { isDefault: false } }))
    }

    return Promise.all(requests)
      .then(() => {
        const updatedConditions = conditions.map(condition => {
          if (!currentDefaultCondition.isDefault && currentDefaultCondition.id === condition.id) {
            return { ...condition, isDefault: false }
          }

          return condition.id === id ? { ...condition, ...data } : condition
        })
        const savedCondition = updatedConditions.find(condition => condition.id === id)

        dispatch(setPayConditions({ conditions: updatedConditions }))
        dispatch(closeFormModal())
        toast.showToast({ message: lang.payConditionSaveSuccessMessage })
        return savedCondition
      })
      .catch(() => {
        toast.showToast({ message: lang.defaultErrorMessage, type: 'error' })
        return Promise.reject()
      })
      .finally(() => dispatch(hideModalLoader()))
  }
}

export const deleteCondition = (id) => {
  return (dispatch, getState) => {
    request(`${settingsConfig.payConditions}/${id}`, { method: 'DELETE', auth: true })
      .then(() => {
        const { settings: { payConditions: { conditions } } } = getState()
        const filteredConditions = conditions.filter(condition => condition.id !== id)

        dispatch(setPayConditions({ conditions: filteredConditions }))
        toast.showToast({ message: lang.payConditionDeleteSuccessMessage })
      })
      .catch((error) => {
        let errorMessage = lang.payConditionDeleteErrorMessage
        if (error?.body?.meta?.id && error.body.meta.id[0].code === errorCodes.NOT_ALLOWED) {
          errorMessage = lang.payConditionIsDefaultDeleteErrorMessage
        }

        console.error(error)
        toast.showToast({ type: 'error', message: errorMessage })
      })
  }
}
